import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/lib/auth/store';
import { useEditTts, usePromptCategories } from '../api';
import type { TtsHistoryRow } from '../types';

interface TtsEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: TtsHistoryRow | null;
}

const LANGUAGES = [
  'English',
  'Hindi',
  'Marathi',
  'Tamil',
  'Telugu',
  'Bengali',
  'Gujarati',
  'Kannada',
  'Malayalam',
  'Punjabi',
];

const GENDERS = ['Male', 'Female'];

/**
 * Small in-place editor for a Saved TTS clip. Mirrors the legacy EditTTS
 * page, but stays inside the Voice Library so users don't context-switch.
 *
 * Submits POST /api/obd/edit/tts with the legacy field names — see
 * `useEditTts` in voice-library/api.ts for the wire shape.
 */
export function TtsEditDialog({ open, onOpenChange, row }: TtsEditDialogProps) {
  const userId = useAuthStore((s) => s.userId);
  const edit = useEditTts();
  const categories = usePromptCategories();

  const [language, setLanguage] = useState('');
  const [gender, setGender] = useState('');
  const [category, setCategory] = useState('');
  const [filename, setFilename] = useState('');
  const [textScript, setTextScript] = useState('');

  // Hydrate from the row whenever the dialog opens for a new clip.
  useEffect(() => {
    if (!open || !row) return;
    setLanguage(String(row.language ?? ''));
    setGender(String(row.gender ?? ''));
    setCategory(String(row.category ?? ''));
    setFilename(row.filename ?? '');
    setTextScript(row.textScript ?? '');
  }, [open, row]);

  if (!row) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!language || !gender || !filename.trim() || !textScript.trim()) {
      toast.error('Language, gender, filename and text are all required.');
      return;
    }
    edit.mutate(
      {
        id: row.id,
        language,
        gender,
        category,
        filename: filename.trim(),
        textScript: textScript.trim(),
        characterCount: textScript.trim().length,
        userId: userId ?? '',
      },
      {
        onSuccess: (res) => {
          if (res?.message === 'Parameters Incorrect') {
            toast.error(res.message);
            return;
          }
          toast.success(res?.message ?? 'TTS clip updated');
          onOpenChange(false);
        },
        onError: () => toast.error('Could not update the TTS clip.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit TTS clip</DialogTitle>
          <DialogDescription>
            Tweak the language, gender, category, filename, or text script.
            Saving regenerates the audio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="language">
                Language<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language">
                  <SelectValue placeholder="Pick a language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="gender">
                Gender<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger id="gender">
                  <SelectValue placeholder="Pick a gender" />
                </SelectTrigger>
                <SelectContent>
                  {GENDERS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category">
                <SelectValue
                  placeholder={
                    categories.isLoading
                      ? 'Loading categories…'
                      : 'Optional'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(categories.data ?? []).map((c) => (
                  <SelectItem key={String(c.id)} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="filename">
              Filename<span className="ml-0.5 text-destructive">*</span>
            </Label>
            <Input
              id="filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="textScript">
              Text script<span className="ml-0.5 text-destructive">*</span>
            </Label>
            <Textarea
              id="textScript"
              rows={5}
              value={textScript}
              onChange={(e) => setTextScript(e.target.value)}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {textScript.length} characters · regenerates the audio on save
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={edit.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={edit.isPending}>
              {edit.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
